import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { BillingScreenLayout } from './billing-screen-layout.component';
import { CancelWithConfirm } from './cancel-with-confirm.component';
import { formatLongDate } from './format';
import { SummaryRow } from './summary-row.component';

import {
  Company,
  CreditLine,
  cancelCreditLine,
  extractBackendErrorMessage,
  postCreditLine,
} from '@/services/hsm-backend';

export interface CreditLineFormProps {
  company: Company;
  refetch: () => Promise<void>;
}

interface CreditLineFormValues {
  messageQty: number | '';
}

/**
 * "Línea de crédito" screen. Left card shows the current credit line
 * (status chip + big "X mensajes disponibles" + metadata). Right card
 * hosts the form to POST a new line. The endpoint **overwrites** the
 * available balance — the form makes that explicit with an Alert.
 *
 * @param company Loaded company from the shell's outlet context.
 * @param refetch Callback that retriggers the shell's getCompany.
 */
export const CreditLineForm = ({ company, refetch }: CreditLineFormProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const creditLine = company.subscription?.creditLine;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreditLineFormValues>({
    defaultValues: { messageQty: creditLine?.messageQty ?? '' },
  });

  const onSubmit = async (values: CreditLineFormValues) => {
    try {
      await postCreditLine(company.companyId, {
        messageQty: Number(values.messageQty),
      });
      enqueueSnackbar('Línea de crédito actualizada', { variant: 'success' });
      await refetch();
      reset({ messageQty: Number(values.messageQty) });
    } catch (error) {
      enqueueSnackbar(
        `No se pudo actualizar la línea: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    }
  };

  return (
    <BillingScreenLayout
      title="Línea de crédito"
      subtitle="Saldo prepago de mensajes que la empresa puede consumir cuando se agota plan y paquetes."
      currentCard={
        <CurrentCreditLineCard
          creditLine={creditLine}
          companyId={company.companyId}
          refetch={refetch}
        />
      }
      formCard={
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box
              component="form"
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              noValidate
            >
              <Typography variant="h6">Editar línea de crédito</Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Cantidad de mensajes"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mensajes</InputAdornment>
                    ),
                  }}
                  helperText={
                    errors.messageQty?.message ??
                    'Reemplaza el saldo actual por este valor.'
                  }
                  error={Boolean(errors.messageQty)}
                  {...register('messageQty', {
                    required: 'Requerido',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Debe ser mayor a 0' },
                  })}
                />
                <Alert severity="warning">
                  Esta operación <strong>sobrescribe</strong> el límite de la
                  línea de crédito. Si querés sumar al saldo, usá un paquete.
                </Alert>
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{ mt: 3 }}
              >
                <Button
                  variant="text"
                  onClick={() => navigate(`/company/${company.companyId}`)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  Guardar cambios
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      }
    />
  );
};

interface CurrentCreditLineCardProps {
  creditLine?: CreditLine;
  companyId: string;
  refetch: () => Promise<void>;
}

const CurrentCreditLineCard = ({
  creditLine,
  companyId,
  refetch,
}: CurrentCreditLineCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Línea actual</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {creditLine && (
            <Chip
              size="small"
              label={creditLine.status === 'active' ? 'Activa' : 'Inactiva'}
              color={creditLine.status === 'active' ? 'success' : 'default'}
            />
          )}
          {creditLine?.status === 'active' && (
            <CancelWithConfirm
              buttonLabel="Cancelar"
              title="Cancelar línea de crédito"
              description="La línea de crédito quedará inactiva y su saldo dejará de estar disponible."
              confirmLabel="Cancelar línea"
              successMessage="Línea de crédito cancelada"
              errorMessagePrefix="No se pudo cancelar la línea de crédito"
              onConfirm={() => cancelCreditLine(companyId)}
              onConfirmed={refetch}
            />
          )}
        </Stack>
      </Stack>
      <Divider sx={{ mt: 1, mb: 2 }} />
      {creditLine ? (
        <>
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="p" fontWeight={700}>
              {creditLine.messageQty.toLocaleString('es-AR')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              mensajes disponibles
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <SummaryRow
              label="Estado"
              value={creditLine.status === 'active' ? 'Activa' : 'Inactiva'}
            />
            <SummaryRow
              label="Creada el"
              value={formatLongDate(creditLine.createdAt)}
            />
            <SummaryRow
              label="Última actualización"
              value={formatLongDate(creditLine.updatedAt)}
            />
          </Stack>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          La empresa no tiene una línea de crédito.
        </Typography>
      )}
    </CardContent>
  </Card>
);
